import { ChatMessageDTO, ChatRoomDTO, ChatRoomView } from './MessageType';
import { getCardBriefInfo } from './cards/MessageCardRegistry';

export const MESSAGE_HISTORY_DAYS = 30;
export const MESSAGE_HISTORY_MS = MESSAGE_HISTORY_DAYS * 24 * 60 * 60 * 1000;

export function getHistoryCutoffTime(): Date {
  return new Date(Date.now() - MESSAGE_HISTORY_MS);
}

export function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

export function formatLastActiveTime(time: Date | string): string {
  const now = new Date();
  const date = toDate(time);
  if (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  ) {
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate()
  ) {
    return '昨日';
  }
  if (date.getFullYear() === now.getFullYear()) {
    return `${date.getMonth() + 1}月${date.getDate()}日`;
  }
  return `${date.getFullYear()}年`;
}

export function formatMessageTime(time: Date | string): string {
  const date = toDate(time);
  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
}

/** 老版本/无法解析的系统 JSON 等，前端直接不展示。 */
export function isUndisplayableMessage(msg: ChatMessageDTO): boolean {
  const content = (msg.content || '').trim();
  if (!content) {
    return true;
  }
  if (content.includes('老版本') && content.includes('不支持展示')) {
    return true;
  }
  if (msg.isSystem && looksLikeJsonPayload(content)) {
    return true;
  }
  return false;
}

function looksLikeJsonPayload(content: string): boolean {
  if (!(content.startsWith('{') || content.startsWith('['))) {
    return false;
  }
  try {
    JSON.parse(content);
    return true;
  } catch {
    return false;
  }
}

export function messageBrief(msg: ChatMessageDTO): string {
  if (isUndisplayableMessage(msg)) {
    return '';
  }
  if (msg.isWithdraw) {
    return '消息已撤回';
  }
  if (msg.isSystem) {
    return msg.content;
  }
  if (msg.isCard) {
    try {
      const card = JSON.parse(msg.content);
      return getCardBriefInfo(card);
    } catch {
      return '[卡片消息]';
    }
  }
  return msg.content;
}

export function isWithinHistoryWindow(time: Date | string, cutoff: Date): boolean {
  return toDate(time).getTime() >= cutoff.getTime();
}

export function roomListSortKey(room: ChatRoomView): number {
  const lm = room.lastMessageTime.getTime();
  if (Number.isFinite(lm) && lm > 0) {
    return lm;
  }
  return toDate(room.lastActiveTime).getTime();
}

export function computeUnreadCount(
  room: ChatRoomDTO,
  messages: ChatMessageDTO[],
  selfUserId: string,
): number {
  const readSeq = room.readSeqMap?.[selfUserId] ?? 0;
  return messages.filter(
    (m) =>
      m.fromUserId !== selfUserId &&
      !m.isWithdraw &&
      !m.isSystem &&
      m.seqInRoom > readSeq,
  ).length;
}

/** chatSide：当前登录端，买家 Web 传 buyer，卖家 Web 传 seller */
export function getOtherUserId(
  room: ChatRoomDTO,
  selfUserId: string,
  chatSide: 'buyer' | 'seller' = 'buyer',
): string | undefined {
  if (!room.memberRoleMap) {
    return undefined;
  }
  const entries = Object.entries(room.memberRoleMap);
  if (!room.isGroup) {
    const peerRole = chatSide === 'buyer' ? 'seller' : 'buyer';
    const byRole = entries.find(([, role]) => role === peerRole);
    if (byRole) {
      return byRole[0];
    }
  }
  if (selfUserId) {
    const other = entries.find(([id]) => id !== selfUserId);
    return other?.[0];
  }
  const seller = entries.find(([, role]) => role === 'seller');
  if (seller) {
    return seller[0];
  }
  const buyer = entries.find(([, role]) => role === 'buyer');
  if (buyer) {
    return buyer[0];
  }
  return entries[0]?.[0];
}

export function isVideoUrl(url: string): boolean {
  return /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
}
